import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useEffect, lazy, Suspense } from 'react';
import { useAuthStore } from './stores/authStore';
import { initializeCurrencyDetection } from './stores/currencyStore';
import MainLayout from './layouts/MainLayout';
import AuthLayout from './layouts/AuthLayout';
import DashboardLayout from './layouts/DashboardLayout';

// Pages
import Home from './pages/Home';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Sellers from './pages/Sellers';
import SellerStore from './pages/SellerStore';
import SellerApply from './pages/SellerApply';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import DiscordCallback from './pages/auth/DiscordCallback';
import Dashboard from './pages/dashboard/Dashboard';
import SellerDashboard from './pages/dashboard/SellerDashboard';
import Orders from './pages/dashboard/Orders';
import OrderDetail from './pages/dashboard/OrderDetail';
import Purchases from './pages/dashboard/Purchases';
import Settings from './pages/dashboard/Settings';
import Notifications from './pages/dashboard/Notifications';
import UserProfile from './pages/UserProfile';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Refunds from './pages/Refunds';
import NotFound from './pages/NotFound';

// Support pages
import Support from './pages/support/Support';
import TicketDetail from './pages/support/TicketDetail';

// Admin pages — lazy loaded so they are split into separate chunks
// and never sent to browsers until a privileged user navigates to /admin/*
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminUsers = lazy(() => import('./pages/admin/Users'));
const AdminCategories = lazy(() => import('./pages/admin/Categories'));
const AdminProducts = lazy(() => import('./pages/admin/Products'));
const AdminSellerApplications = lazy(() => import('./pages/admin/SellerApplications'));
const AdminSupport = lazy(() => import('./pages/admin/Support'));
const AdminSupportTicketDetail = lazy(() => import('./pages/admin/SupportTicketDetail'));
const AdminForumMod = lazy(() => import('./pages/admin/ForumMod'));
const AdminAnalytics = lazy(() => import('./pages/admin/Analytics'));
const AdminPendingKeyDeliveries = lazy(() => import('./pages/admin/PendingKeyDeliveries'));

// Route-level guard: redirects before lazy chunks are ever fetched,
// so non-admin users never download admin JS.
function AdminGuard() {
  const { user, isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) return null;

  const canAccess =
    isAuthenticated &&
    (user?.role === 'owner' || user?.role === 'admin' || user?.role === 'moderator');

  if (!canAccess) return <Navigate to="/" replace />;

  return <Outlet />;
}

function App() {
  const { checkAuth } = useAuthStore();

  useEffect(() => {
    checkAuth();
    // Initialize currency detection from IP
    initializeCurrencyDetection();
  }, [checkAuth]);

  return (
    <>
      {/* Grain overlay for texture */}
      <div className="grain-overlay" />

      <Routes>
        {/* Public routes */}
        <Route element={<MainLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/:id" element={<ProductDetail />} />
          <Route path="/sellers" element={<Sellers />} />
          <Route path="/sellers/apply" element={<SellerApply />} />
          <Route path="/sellers/:slug" element={<SellerStore />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/support" element={<Support />} />
          <Route path="/support/:id" element={<TicketDetail />} />
          <Route path="/user/:username" element={<UserProfile />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/refunds" element={<Refunds />} />
        </Route>

        {/* Auth routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/auth/discord/callback" element={<DiscordCallback />} />
        </Route>

        {/* Dashboard routes */}
        <Route element={<DashboardLayout />}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/dashboard/orders" element={<Orders />} />
          <Route path="/dashboard/orders/:id" element={<OrderDetail />} />
          <Route path="/dashboard/purchases" element={<Purchases />} />
          <Route path="/dashboard/notifications" element={<Notifications />} />
          <Route path="/dashboard/settings" element={<Settings />} />
          <Route path="/dashboard/seller" element={<SellerDashboard />} />
        </Route>

        {/* Admin routes — guard runs before any lazy chunk is fetched */}
        <Route element={<AdminGuard />}>
          <Route element={<Suspense fallback={null}><Outlet /></Suspense>}>
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/categories" element={<AdminCategories />} />
            <Route path="/admin/products" element={<AdminProducts />} />
            <Route path="/admin/sellers" element={<AdminSellerApplications />} />
            <Route path="/admin/support" element={<AdminSupport />} />
            <Route path="/admin/support/:id" element={<AdminSupportTicketDetail />} />
            <Route path="/admin/forum" element={<AdminForumMod />} />
            <Route path="/admin/forum/categories" element={<Navigate to="/admin/forum" replace />} />
            <Route path="/admin/analytics" element={<AdminAnalytics />} />
            <Route path="/admin/pending-keys" element={<AdminPendingKeyDeliveries />} />
          </Route>
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}

export default App;
